/**
 * 网络管理器 - 用于MMO游戏的网络通信
 * 支持WebSocket连接，处理玩家同步、消息传递等
 */
import DebugHelper from './DebugHelper.js';
import EventBus from './EventBus.js';
import GameStateStore from './GameStateStore.js';

const NETWORK_EVENT_PREFIX = 'network:';

export default class NetworkManager {
    constructor(scene) {
        this.scene = scene;
        this.socket = null;
        this.connected = false;
        this.serverUrl = '';
        this.roomId = '';
        this.playerId = null;
        this.players = new Map(); // 存储其他玩家信息
        
        // 重连配置
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
    }
    
    /**
     * 连接到服务器
     * @param {string} serverUrl - 服务器地址
     * @param {string} roomId - 房间ID（可选）
     * @param {string} playerName - 玩家名称
     * @param {Object} playerData - 玩家数据
     */
    connect(serverUrl, roomId = '', playerName = 'Player', playerData = {}) {
        this._manualDisconnect = false;
        this._cleanupSocket();

        this.serverUrl = serverUrl;
        this.roomId = roomId;
        
        // 验证并设置玩家名称：如果为空或未定义，尝试从 gameState 或 userInfo 获取，否则使用默认值
        if (!playerName || playerName.trim() === '') {
            playerName = GameStateStore.getPlayer()?.name ||
                        window.userInfo?.username || 
                        '玩家';
        }
        this.playerName = playerName.trim();
        this.playerData = playerData;
        
        try {
            // 创建WebSocket连接
            this.socket = new WebSocket(serverUrl);
            
            // 连接成功
            this.socket.onopen = () => {
                DebugHelper.log('网络连接已建立');
                this.connected = true;
                this.reconnectAttempts = 0;
                
                // 注意：连接服务器和加入房间是两个独立的操作
                // 只有在明确调用 joinRoom() 时才发送 join 消息
                // 这里不再自动发送 join 消息，避免未登录用户自动加入房间
                
                this._emit('connect');
            };
            
            // 接收消息
            this.socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.handleMessage(data);
                } catch (error) {
                    DebugHelper.error('解析消息失败:', error);
                }
            };
            
            // 连接关闭
            this.socket.onclose = () => {
                DebugHelper.log('网络连接已关闭');
                this.connected = false;
                this.socket = null;
                
                this._emit('disconnect');
                
                // 非主动断开时尝试重连
                if (!this._manualDisconnect) {
                    this.attemptReconnect();
                }
            };
            
            // 连接错误
            this.socket.onerror = (error) => {
                DebugHelper.error('网络连接错误:', error);
                this.connected = false;
                
                this._emit('error', error);
            };
            
        } catch (error) {
            DebugHelper.error('创建连接失败:', error);
            this.connected = false;
            this._emit('error', error);
        }
    }
    
    /**
     * 处理接收到的消息
     * @param {Object} data - 消息数据
     */
    handleMessage(data) {
        const { type, payload } = data;
        
        switch (type) {
            case 'joined':
                // 成功加入房间
                this.playerId = payload.playerId;
                this.roomId = payload.roomId;
                if (payload.players) {
                    payload.players.forEach(player => {
                        this.players.set(player.id, player);
                    });
                }
                DebugHelper.log('已加入房间，玩家ID:', this.playerId);
                this._emit('joined', payload);
                break;
                
            case 'playerJoin':
                // 新玩家加入
                DebugHelper.log('NetworkManager: 收到 playerJoin 消息', {
                    playerId: payload.playerId,
                    playerData: payload.playerData,
                    playerName: payload.playerData?.playerName,
                    element: payload.playerData?.element
                });
                this.players.set(payload.playerId, payload.playerData);
                this._emit('playerJoin', payload.playerId, payload.playerData);
                break;
                
            case 'playerLeave':
                // 玩家离开
                this.players.delete(payload.playerId);
                this._emit('playerLeave', payload.playerId);
                break;
                
            case 'playerUpdate':
                // 玩家状态更新
                if (this.players.has(payload.playerId)) {
                    const player = this.players.get(payload.playerId);
                    // 只更新 payload.playerData 中存在的字段，避免覆盖未提供的属性
                    // 这样可以防止发送空数据时覆盖掉原有属性
                    if (payload.playerData) {
                        Object.keys(payload.playerData).forEach(key => {
                            if (payload.playerData[key] !== undefined && payload.playerData[key] !== null) {
                                // 位置更新不应该覆盖玩家的基础信息（如 playerName, element）
                                // 只有位置相关的字段（x, y, direction）才允许更新
                                if (['x', 'y', 'direction'].includes(key)) {
                                    player[key] = payload.playerData[key];
                                } else {
                                    // 对于其他字段，也允许更新（如 hp, money 等状态）
                                    // 但不应该包含 element，因为 element 不应该通过 playerUpdate 更新
                                    if (key !== 'element') {
                                        player[key] = payload.playerData[key];
                                    } else {
                                        DebugHelper.warn(`NetworkManager: 收到 playerUpdate 消息包含 element 字段，忽略。playerId=${payload.playerId}, element=${payload.playerData[key]}`);
                                    }
                                }
                            }
                        });
                    }
                }
                this._emit('playerUpdate', payload.playerId, payload.playerData);
                break;
                
            case 'message':
                // 聊天消息
                this._emit('message', payload);
                break;
                
            case 'roomList':
                // 房间列表
                this._emit('roomList', payload.rooms || []);
                break;
                
            case 'roomCreated':
                // 房间创建成功
                this._emit('roomCreated', payload.roomId, payload.roomInfo);
                break;
                
            case 'teamCreated':
                // 队伍创建成功
                DebugHelper.log('收到teamCreated消息:', payload);
                this._emit('teamCreated', payload.teamId, payload.teamInfo);
                break;
                
            case 'teamInvite':
                // 收到组队邀请
                this._emit('teamInvite', payload);
                break;
                
            case 'teamInviteSent':
                // 邀请已发送
                this._emit('teamInviteSent', payload);
                break;
                
            case 'teamMemberJoined':
                // 队员加入
                DebugHelper.log('NetworkManager: 收到 teamMemberJoined 消息', payload);
                this._emit('teamMemberJoined', payload);
                break;
                
            case 'teamMemberLeft':
                // 队员离开
                this._emit('teamMemberLeft', payload);
                break;
                
            case 'teamLeft':
                // 离开队伍成功
                this._emit('teamLeft', payload);
                break;
                
            case 'teamDisbanded':
                // 队伍解散
                this._emit('teamDisbanded', payload);
                break;
                
            case 'teamInviteRejected':
                // 邀请被拒绝
                this._emit('teamInviteRejected', payload);
                break;
                
            case 'welcome':
                // 服务器欢迎消息
                DebugHelper.log('服务器欢迎消息:', payload.message);
                break;
                
            case 'pong':
                // 心跳响应
                DebugHelper.log('收到心跳响应');
                break;
                
            case 'error':
                // 服务器错误消息
                DebugHelper.error('服务器错误:', payload.message);
                this._emit('error', new Error(payload.message || '服务器错误'));
                break;
                
            case 'loginSuccess':
                // 登录成功
                this.userId = payload.userId;
                DebugHelper.log('登录成功:', payload);
                this._emit('loginSuccess', payload);
                break;
                
            case 'registerSuccess':
                // 注册成功
                DebugHelper.log('注册成功:', payload);
                this._emit('registerSuccess', payload);
                break;
                
            case 'savePlayerDataSuccess':
                // 保存玩家数据成功
                DebugHelper.log('玩家数据保存成功');
                this._emit('savePlayerDataSuccess', payload);
                break;
                
            case 'loadPlayerDataSuccess':
                // 加载玩家数据成功
                DebugHelper.log('玩家数据加载成功');
                this._emit('loadPlayerDataSuccess', payload);
                break;
                
            case 'battleStart':
                // 战斗开始（组队模式，队长触发战斗时通知队员）
                DebugHelper.log('收到战斗开始消息:', payload);
                this._emit('battleStart', payload);
                break;
                
            case 'battleTurn':
                // 战斗回合切换（组队模式，队员完成操作后通知其他队员）
                DebugHelper.log('收到战斗回合切换消息:', payload);
                this._emit('battleTurn', payload);
                break;
                
            case 'battleAction':
                // 战斗动作（组队模式，队员执行动作后通知其他队员）
                DebugHelper.log('收到战斗动作消息:', payload);
                this._emit('battleAction', payload);
                break;
                
            case 'battleVictory':
                // 战斗胜利（组队模式，队员触发胜利后通知其他队员）
                DebugHelper.log('收到战斗胜利消息:', payload);
                this._emit('battleVictory', payload);
                break;
                
            case 'battleDefeat':
                // 战斗失败（组队模式，队员触发失败后通知其他队员）
                DebugHelper.log('收到战斗失败消息:', payload);
                this._emit('battleDefeat', payload);
                break;
                
            default:
                DebugHelper.log('未知消息类型:', type, payload);
        }
    }
    
    /**
     * 发送消息到服务器
     * @param {string} type - 消息类型
     * @param {Object} data - 消息数据
     */
    send(type, data = {}) {
        if (!this.connected || !this.socket) {
            DebugHelper.warn('未连接，无法发送消息');
            return false;
        }
        
        try {
            const message = JSON.stringify({
                type: type,
                payload: data,
                timestamp: Date.now()
            });
            
            // 不记录频繁的位置更新消息，避免日志过多
            if (type !== 'playerPosition') {
                DebugHelper.log(`发送消息: ${type}`, data);
            }
            this.socket.send(message);
            return true;
        } catch (error) {
            DebugHelper.error('发送消息失败:', error);
            return false;
        }
    }
    
    /**
     * 更新玩家位置
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {string} direction - 方向
     */
    updatePlayerPosition(x, y, direction) {
        this.send('playerPosition', {
            x: x,
            y: y,
            direction: direction
        });
    }
    
    /**
     * 更新玩家状态
     * @param {Object} playerData - 玩家数据
     */
    updatePlayerState(playerData) {
        this.send('playerState', playerData);
    }
    
    /**
     * 发送聊天消息
     * @param {string} message - 消息内容
     */
    sendMessage(message) {
        this.send('chat', {
            message: message,
            playerName: this.playerName
        });
    }
    
    /**
     * 清理旧 WebSocket 连接，防止重连时泄漏
     */
    _cleanupSocket() {
        if (!this.socket) {
            return;
        }

        const socket = this.socket;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;

        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close();
        }

        this.socket = null;
        this.connected = false;
    }

    /**
     * 尝试重连
     */
    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            DebugHelper.log('达到最大重连次数，停止重连');
            return;
        }
        
        this.reconnectAttempts++;
        DebugHelper.log(`尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        
        // 确保 playerName 不为空（重连时可能 playerName 已被清空）
        let playerName = this.playerName;
        if (!playerName || playerName.trim() === '') {
            playerName = GameStateStore.getPlayer()?.name ||
                        window.userInfo?.username || 
                        '玩家';
        }
        
        setTimeout(() => {
            this.connect(this.serverUrl, this.roomId, playerName, this.playerData);
        }, this.reconnectDelay);
    }
    
    /**
     * 断开连接
     */
    disconnect() {
        this._manualDisconnect = true;
        this.reconnectAttempts = this.maxReconnectAttempts;

        if (this.socket) {
            if (this.connected) {
                this.send('leave', {});
            }
            this._cleanupSocket();
        }
        this.players.clear();
    }
    
    /**
     * @param {string} event
     * @param {...*} args
     */
    _emit(event, ...args) {
        EventBus.emit(NETWORK_EVENT_PREFIX + event, ...args);
    }

    _toEventName(event) {
        return NETWORK_EVENT_PREFIX + event;
    }

    /**
     * 注册事件处理器（支持多订阅，不会互相覆盖）
     * @param {string} event - 事件名称
     * @param {Function} handler - 处理函数
     * @returns {Function} unsubscribe
     */
    on(event, handler) {
        return EventBus.on(this._toEventName(event), handler);
    }

    /**
     * 注册一次性事件处理器
     * @param {string} event
     * @param {Function} handler
     * @returns {Function} unsubscribe
     */
    once(event, handler) {
        return EventBus.once(this._toEventName(event), handler);
    }

    /**
     * 移除事件处理器
     * @param {string} event - 事件名称
     * @param {Function} [handler] - 不传则移除该事件全部监听
     */
    off(event, handler) {
        EventBus.off(this._toEventName(event), handler);
    }
    
    /**
     * 获取所有在线玩家
     * @returns {Array} 玩家列表
     */
    getPlayers() {
        return Array.from(this.players.values());
    }
    
    /**
     * 获取指定玩家
     * @param {string} playerId - 玩家ID
     * @returns {Object|null} 玩家数据
     */
    getPlayer(playerId) {
        return this.players.get(playerId) || null;
    }
    
    /**
     * 检查是否已连接
     * @returns {boolean}
     */
    isConnected() {
        return this.connected;
    }
    
    /**
     * 获取房间列表
     */
    getRoomList() {
        this.send('getRoomList', {});
    }
    
    /**
     * 创建房间
     * @param {string} roomName - 房间名称
     * @param {number} maxPlayers - 最大玩家数
     */
    createRoom(roomName, maxPlayers = 10) {
        this.send('createRoom', {
            roomName: roomName,
            maxPlayers: maxPlayers
        });
    }
    
    /**
     * 创建队伍
     */
    createTeam() {
        this.send('teamCreate', {});
    }
    
    /**
     * 邀请玩家加入队伍
     * @param {string} toPlayerId - 被邀请玩家ID
     */
    invitePlayerToTeam(toPlayerId) {
        this.send('teamInvite', {
            toPlayerId: toPlayerId
        });
    }
    
    /**
     * 接受组队邀请
     * @param {string} inviteId - 邀请ID
     */
    acceptTeamInvite(inviteId) {
        DebugHelper.log('NetworkManager: acceptTeamInvite 被调用', { inviteId: inviteId });
        this.send('teamAccept', {
            inviteId: inviteId
        });
        DebugHelper.log('NetworkManager: 已发送 teamAccept 消息');
    }
    
    /**
     * 拒绝组队邀请
     * @param {string} inviteId - 邀请ID
     */
    rejectTeamInvite(inviteId) {
        this.send('teamReject', {
            inviteId: inviteId
        });
    }
    
    /**
     * 离开队伍
     */
    leaveTeam() {
        this.send('teamLeave', {});
    }
    
    /**
     * 解散队伍
     */
    disbandTeam() {
        this.send('teamDisband', {});
    }
    
    /**
     * 通知队伍成员战斗开始（仅队长调用）
     * @param {Object} battleData - 战斗数据
     */
    notifyTeamBattleStart(battleData) {
        this.send('teamBattleStart', battleData);
    }
    
    /**
     * 通知队伍成员战斗回合切换（队员完成操作后调用）
     * @param {number} newTeamMemberIndex - 新的队员索引
     */
    notifyTeamBattleTurn(newTeamMemberIndex) {
        this.send('teamBattleTurn', { teamMemberIndex: newTeamMemberIndex });
    }
    
    /**
     * 通知队伍成员战斗动作（攻击、防御、技能等）
     * @param {Object} actionData - 动作数据 { action, targetIndex, damage, etc. }
     */
    notifyTeamBattleAction(actionData) {
        this.send('teamBattleAction', actionData);
    }
    
    /**
     * 通知队伍成员战斗胜利
     * @param {Object} victoryData - 胜利数据 { defeatedEnemies: [] }
     */
    notifyTeamBattleVictory(victoryData) {
        this.send('teamBattleVictory', victoryData);
    }
    
    /**
     * 通知队伍成员战斗失败
     */
    notifyTeamBattleDefeat() {
        this.send('teamBattleDefeat', {});
    }
}

